// Scrivener RTF → the prose Markdown dialect, in the browser (`RTFConverter`, SuiteImport). The Mac hands the RTF to
// `NSAttributedString` and walks the result; here the RTF is read directly into the same shape — a string of
// characters with a run of attributes over each, and paragraph properties — and then the Mac's paragraph rules run over
// it unchanged: scene-break markers, lists from the list table or typed into the text, block quotes by indent, centre
// and right alignment, emphasis from the font as well as `\b`/`\i`, underline, highlights by hue, links and linked
// comments as anchors, hard breaks, leading tabs and trailing spaces trimmed. Formatting the dialect can't hold
// (strikethrough, super/subscript, inline images) is reported, never silently dropped. See
// `design-docs/Scrivener import in the browser — design.md`.
//
// Both Cocoa's writer (`\` + newline for a paragraph, italics by switching to the italic face, `\cb` highlights,
// `{\listtext …}` kept as text) and Windows Scrivener's (`\par`, `\highlight`, `\pn` lists with `{\pntext …}`) are read.
import type { Alignment, Block, Inline, InlineStyle, ListStyle } from '../contract.ts';
import { MAX_VERSE_INDENT, parseListMarker, plainStyle as plainInlineStyle, serialize } from '../proseMarkdown.ts';
import { AnchorSentinels } from './anchorSentinels.ts';
import { graphemes, trimCFWhitespaceAndNewlines } from '../unicode.ts';

export type RTFAnchorKind =
  | { type: 'scrivenerComment'; uuid: string }
  | { type: 'documentLink'; uuid: string }
  | { type: 'webLink'; url: string }
  | { type: 'highlight'; color: string };

export interface RTFAnchor {
  kind: RTFAnchorKind;
  /** In Unicode scalars of `markdown`, [from, to). */
  range: [number, number];
}

export interface RTFAttachment { filename: string; data: Uint8Array }

export interface RTFConversion {
  markdown: string;
  /** The RTF's own plain text, for verifying the conversion lost no words. */
  sourcePlainText: string;
  anchors: RTFAnchor[];
  attachments: RTFAttachment[];
  issues: string[];
}

/** `RTFAnchorKind` as one string, for fixtures and keys: `comment:<uuid>`, `link:<uuid>`, `web:<url>`, `highlight:<name>`. */
export function anchorKindKey(k: RTFAnchorKind): string {
  switch (k.type) {
    case 'scrivenerComment': return 'comment:' + k.uuid;
    case 'documentLink': return 'link:' + k.uuid;
    case 'webLink': return 'web:' + k.url;
    case 'highlight': return 'highlight:' + k.color;
  }
}

// MARK: The attributed string

interface Font { name: string; bold: boolean; italic: boolean; symbol: boolean }

interface CharAttrs {
  font: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  /** Super/subscript or a baseline shift. */
  raised: boolean;
  /** The background colour's index (`\cb`, `\highlight`), 0 for none. */
  background: number;
  /** The link a `\fldrslt` is carrying. */
  link: string | null;
}

interface ParaAttrs {
  align: 'left' | 'center' | 'right' | 'justify';
  /** Twips. */
  li: number;
  fi: number;
  /** The list (`\ls`) the paragraph is in, else null. */
  ls: number | null;
  /** A Windows `\pn` list style on the paragraph. */
  pn: ListStyle | null;
}

interface Run { start: number; end: number; attrs: CharAttrs }
interface Paragraph { start: number; end: number; para: ParaAttrs }
interface ImageRef { at: number; attachment: RTFAttachment | null }

interface Attributed {
  text: string;
  runs: Run[];
  paragraphs: Paragraph[];
  images: ImageRef[];
  fonts: Font[];
  colours: ([number, number, number] | null)[];
  issues: IssueCounter;
}

/** Scrivener's paragraph and character style markers and script mode's keep-with-next: formatting, not words. */
const FORMATTING_MARKER = /<!?\$Scr[A-Za-z_]*(?:::\d+)?>/g;

class IssueCounter {
  private counts = new Map<string, number>();
  add(message: string) { this.counts.set(message, (this.counts.get(message) ?? 0) + 1); }
  get messages(): string[] {
    return [...this.counts].map(([m, n]) => (n > 1 ? `${m} (×${n})` : m));
  }
}

// Windows-1252's 0x80–0x9F; the rest is Latin-1.
const CP1252_HIGH = '€\u0081‚ƒ„…†‡ˆ‰Š‹Œ\u008dŽ\u008f\u0090‘’“”•–—˜™š›œ\u009džŸ';
const MAC_ROMAN = 'ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûü†°¢£§•¶ß®©™´¨≠ÆØ∞±≤≥¥µ∂∑∏π∫ªºΩæø¿¡¬√ƒ≈∆«»… ÀÃÕŒœ–—“”‘’÷◊ÿŸ⁄€‹›ﬁﬂ‡·‚„‰ÂÊÁËÈÍÎÏÌÓÔÒÚÛÙıˆ˜¯˘˙˚¸˝˛ˇ';

function decodeByte(byte: number, codePage: number, symbolFont: boolean): string {
  if (symbolFont && byte === 0xb7) return '•';
  if (byte < 0x80) return String.fromCharCode(byte);
  if (codePage === 10000) return MAC_ROMAN[byte - 0x80];
  if (byte < 0xa0) return CP1252_HIGH[byte - 0x80];
  return String.fromCharCode(byte);
}

const fontTraits = (name: string): Pick<Font, 'bold' | 'italic'> => ({
  italic: /italic|oblique/i.test(name),
  bold: /bold|semibold|demibold|black|heavy|extrabold|ultrabold/i.test(name),
});

const CHAR_WORDS: Record<string, string> = {
  tab: '\t', line: '\u2028', emdash: '—', endash: '–', lquote: '‘', rquote: '’', ldblquote: '“', rdblquote: '”',
  bullet: '•', enspace: ' ', emspace: ' ', qmspace: ' ', zwj: '‍', zwnj: '‌', lbr: '\u2028',
};

type Destination = 'skip' | 'fonttbl' | 'colortbl' | 'listtable' | 'fldinst' | 'pict' | 'levelmarker' | 'pn' | 'listoverride' | null;

interface Frame {
  chars: CharAttrs;
  para: ParaAttrs;
  uc: number;
  destination: Destination;
  /** The `\field` this group is in, shared by its `\fldinst` and `\fldrslt`. */
  field: { url: string | null } | null;
  /** Inside the list table: the list and level being read. */
  list: { id: number | null; levels: { format: string; nfc: number; startAt: number }[] } | null;
  level: { format: string; nfc: number; startAt: number } | null;
  override: { listId: number | null; ls: number | null } | null;
  pict: { hex: string; kind: string } | null;
  /** A Windows `\pn` being read. */
  pn: { style: ListStyle } | null;
}

/** The RTF read into Cocoa's shape. */
function read(bytes: Uint8Array): Attributed {
  const fonts: Font[] = [];
  const colours: ([number, number, number] | null)[] = [null];
  const lists = new Map<number, { format: string; nfc: number; startAt: number }[]>();
  const overrides = new Map<number, number>();
  const issues = new IssueCounter();
  let codePage = 1252;
  let text = '';
  const runs: Run[] = [];
  const paragraphs: Paragraph[] = [];
  const images: ImageRef[] = [];
  let paragraphStart = 0;
  let paragraphProps: ParaAttrs | null = null;

  const plainChars = (): CharAttrs => ({ font: 0, bold: false, italic: false, underline: false, strike: false, raised: false, background: 0, link: null });
  const plainPara = (): ParaAttrs => ({ align: 'left', li: 0, fi: 0, ls: null, pn: null });
  const root: Frame = { chars: plainChars(), para: plainPara(), uc: 1, destination: null, field: null, list: null, level: null, override: null, pict: null, pn: null };
  const stack: Frame[] = [root];
  let frame = root;
  let skipChars = 0;
  let dropAttachmentChar = false;
  // The colour table and the font table are text with separators; collected here.
  let tableText = '';
  let tableColour: [number, number, number] = [0, 0, 0];
  let tableFontIndex = 0;

  const effective = (f: Frame): CharAttrs => {
    const font = fonts[f.chars.font];
    return { ...f.chars, bold: f.chars.bold || !!font?.bold, italic: f.chars.italic || !!font?.italic, link: f.field?.url ?? f.chars.link };
  };

  const emit = (s: string) => {
    if (!s) return;
    if (frame.destination === 'fonttbl' || frame.destination === 'colortbl' || frame.destination === 'levelmarker') { tableText += s; return; }
    if (frame.destination === 'fldinst') { tableText += s; return; }
    if (frame.destination === 'pict') { if (frame.pict) frame.pict.hex += s; return; }
    if (frame.destination) return;
    if (paragraphProps === null) paragraphProps = { ...frame.para };
    const attrs = effective(frame);
    const last = runs[runs.length - 1];
    const start = text.length;
    text += s;
    if (last && last.end === start && sameAttrs(last.attrs, attrs)) last.end = text.length;
    else runs.push({ start, end: text.length, attrs });
  };

  const endParagraph = () => {
    if (frame.destination) return;
    const props = paragraphProps ?? { ...frame.para };
    emit('\n');
    paragraphs.push({ start: paragraphStart, end: text.length, para: props });
    paragraphStart = text.length;
    paragraphProps = null;
  };

  const flushTable = () => {
    if (frame.destination === 'fonttbl') {
      for (const entry of tableText.split(';')) {
        const name = entry.trim();
        if (!name) continue;
        fonts[tableFontIndex] = { name, ...fontTraits(name), symbol: fonts[tableFontIndex]?.symbol ?? false };
      }
      tableText = '';
    } else if (frame.destination === 'colortbl') {
      // Entries end in ';'; the first, empty one is "auto".
      for (const entry of tableText.split(';').slice(0, -1)) {
        void entry;
      }
    }
  };

  let i = 0;
  const n = bytes.length;
  const readNumber = (): number | null => {
    let j = i, negative = false;
    if (bytes[j] === 0x2d) { negative = true; j++; }
    const startDigits = j;
    while (j < n && bytes[j] >= 0x30 && bytes[j] <= 0x39) j++;
    if (j === startDigits) return null;
    const value = parseInt(String.fromCharCode(...bytes.subarray(startDigits, j)), 10);
    i = j;
    return negative ? -value : value;
  };

  while (i < n) {
    const b = bytes[i];
    if (b === 0x7b) { // {
      i++;
      const child: Frame = { ...frame, chars: { ...frame.chars }, para: { ...frame.para }, level: null };
      stack.push(child);
      frame = child;
      continue;
    }
    if (b === 0x7d) { // }
      i++;
      // Leaving a destination: finish what it collected.
      const leaving = frame;
      if (leaving.destination === 'fldinst' && leaving.field) {
        const m = /HYPERLINK\s+"?([^"\s]+)"?/.exec(tableText);
        if (m) leaving.field.url = m[1];
        tableText = '';
      } else if (leaving.destination === 'levelmarker' && leaving.level) {
        leaving.level.format = tableText.trim();
        tableText = '';
      } else if (leaving.destination === 'pict' && leaving.pict) {
        const data = hexBytes(leaving.pict.hex);
        const ext = leaving.pict.kind === 'jpegblip' ? 'jpg' : leaving.pict.kind === 'pngblip' ? 'png' : leaving.pict.kind === 'emfblip' ? 'emf' : 'wmf';
        images.push({ at: text.length, attachment: data.length ? { filename: `image.${ext}`, data } : null });
        // Cocoa puts an attachment character where the picture was.
        frame = stack[stack.length - 2] ?? root;
        emit('\ufffc');
        frame = leaving;
      }
      stack.pop();
      const parent = stack[stack.length - 1] ?? root;
      if (leaving.destination === 'fonttbl' && parent.destination !== 'fonttbl') flushTable();
      if (leaving.destination === 'colortbl' && parent.destination !== 'colortbl') {
        // The last entry (after the final ';') is empty.
        tableText = '';
      }
      if (leaving.list && parent.list !== leaving.list) {
        // A `\list` group closed: file it by id.
        if (leaving.list.id !== null) lists.set(leaving.list.id, leaving.list.levels);
      }
      if (leaving.level && parent.level !== leaving.level && leaving.list) leaving.list.levels.push(leaving.level);
      if (leaving.override && parent.override !== leaving.override && leaving.override.ls !== null && leaving.override.listId !== null) {
        overrides.set(leaving.override.ls, leaving.override.listId);
      }
      frame = parent;
      continue;
    }
    if (b === 0x5c) { // backslash
      i++;
      if (i >= n) break;
      const c = bytes[i];
      if (c === 0x27) { // \'xx
        const hex = String.fromCharCode(bytes[i + 1] ?? 0x30, bytes[i + 2] ?? 0x30);
        i += 3;
        if (skipChars > 0) { skipChars--; continue; }
        // The ¬ after a NeXTGraphic group is Cocoa's attachment character, not text.
        if (dropAttachmentChar && hex.toLowerCase() === 'ac') { dropAttachmentChar = false; emit('\ufffc'); continue; }
        dropAttachmentChar = false;
        const font = fonts[frame.chars.font];
        emit(decodeByte(parseInt(hex, 16), codePage, !!font?.symbol));
        continue;
      }
      if (c === 0x5c || c === 0x7b || c === 0x7d) { i++; if (skipChars > 0) { skipChars--; continue; } emit(String.fromCharCode(c)); continue; }
      if (c === 0x7e) { i++; if (skipChars > 0) { skipChars--; continue; } emit('\u00a0'); continue; } // \~
      if (c === 0x2d) { i++; if (skipChars > 0) skipChars--; continue; } // \- optional hyphen
      if (c === 0x5f) { i++; if (skipChars > 0) { skipChars--; continue; } emit('\u2011'); continue; } // \_
      if (c === 0x2a) { // \* ignorable destination: skipped unless known
        i++;
        continue; // the control word that follows decides
      }
      if (c === 0x0a || c === 0x0d) { // \ + newline = \par
        i++;
        if (c === 0x0d && bytes[i] === 0x0a) i++;
        endParagraph();
        continue;
      }
      if (!((c >= 0x61 && c <= 0x7a) || (c >= 0x41 && c <= 0x5a))) { i++; continue; } // other symbol
      let j = i;
      while (j < n && ((bytes[j] >= 0x61 && bytes[j] <= 0x7a) || (bytes[j] >= 0x41 && bytes[j] <= 0x5a))) j++;
      const word = String.fromCharCode(...bytes.subarray(i, j));
      i = j;
      const param = readNumber();
      if (bytes[i] === 0x20) i++; // the delimiting space
      // Which control words open a destination: the previous byte before this group was `\*` for the ignorable ones.
      const starred = i > 0 && isStarredGroup(bytes, j - word.length - 1);
      control(word, param, starred);
      continue;
    }
    // Plain text, Latin-1 bytes; raw high bytes through the code page as Cocoa's reader does.
    if (b === 0x0a || b === 0x0d) { i++; continue; }
    i++;
    if (skipChars > 0) { skipChars--; continue; }
    if (frame.destination === 'skip' || frame.destination === 'listoverride' || frame.destination === 'listtable' || frame.destination === 'pn') {
      // Text inside tables is markup; the list table's level text and names are never shown.
      if (frame.destination === 'pn') continue;
      continue;
    }
    const font = fonts[frame.chars.font];
    emit(b < 0x80 ? String.fromCharCode(b) : decodeByte(b, codePage, !!font?.symbol));
  }
  if (text.length > paragraphStart || paragraphProps !== null) {
    paragraphs.push({ start: paragraphStart, end: text.length, para: paragraphProps ?? { ...frame.para } });
  }
  // Scrivener's markers go, with the runs and paragraphs shifted.
  const stripped = removeMarkers(text, runs, paragraphs, images);
  return { text: stripped, runs, paragraphs, images, fonts, colours, issues };

  function control(word: string, param: number | null, starred: boolean) {
    switch (word) {
      case 'rtf': case 'ansi': case 'mac': case 'pc': case 'pca': case 'deff': case 'cocoartf': case 'cocoaplatform': case 'cocoatextscaling':
      case 'readonlydoc': case 'viewkind': case 'deftab': case 'pardeftab': case 'partightenfactor': case 'pardirnatural': case 'slmult': case 'sl':
      case 'sa': case 'sb': case 'tx': case 'kerning': case 'expnd': case 'expndtw': case 'fcharset': case 'fprq': case 'froman': case 'fswiss':
      case 'fmodern': case 'fnil': case 'fscript': case 'fdecor': case 'ftech': case 'fbidi': case 'lang': case 'langfe': case 'langnp': case 'ltrpar':
      case 'rtlpar': case 'ltrch': case 'rtlch': case 'loch': case 'hich': case 'dbch': case 'af': case 'afs': case 'nowidctlpar': case 'widctlpar':
      case 'adjustright': case 'faauto': case 'cgrid': case 'ri': case 'outlinelevel': case 'itap': case 'generator': case 'noproof':
        if (word === 'fcharset' && frame.destination === 'fonttbl') {
          if (!fonts[tableFontIndex]) fonts[tableFontIndex] = { name: '', bold: false, italic: false, symbol: false };
          fonts[tableFontIndex].symbol = param === 2;
        }
        return;
      case 'ansicpg': codePage = param ?? 1252; return;
      case 'uc': frame.uc = param ?? 1; return;
      case 'u': {
        let code = param ?? 0;
        if (code < 0) code += 65536;
        skipChars = frame.uc;
        if (!frame.destination || frame.destination === 'fldinst' || frame.destination === 'levelmarker') emit(String.fromCharCode(code));
        return;
      }
      case 'bin': { const length = param ?? 0; i += length; return; }
      case 'par': case 'row': endParagraph(); return;
      case 'sect': case 'page': case 'sectd': case 'column': return;
      case 'pard': frame.para = { align: 'left', li: 0, fi: 0, ls: null, pn: null }; return;
      case 'plain': frame.chars = { font: frame.chars.font, bold: false, italic: false, underline: false, strike: false, raised: false, background: 0, link: frame.chars.link }; return;
      case 'qc': frame.para.align = 'center'; return;
      case 'qr': frame.para.align = 'right'; return;
      case 'qj': frame.para.align = 'justify'; return;
      case 'ql': frame.para.align = 'left'; return;
      case 'li': case 'lin': frame.para.li = param ?? 0; return;
      case 'fi': frame.para.fi = param ?? 0; return;
      case 'ls': frame.para.ls = param ?? null; if (frame.override) frame.override.ls = param ?? null; return;
      case 'ilvl': return;
      case 'f':
        if (frame.destination === 'fonttbl') { flushTable(); tableFontIndex = param ?? 0; if (!fonts[tableFontIndex]) fonts[tableFontIndex] = { name: '', bold: false, italic: false, symbol: false }; return; }
        frame.chars.font = param ?? 0; return;
      case 'fs': return;
      case 'b': frame.chars.bold = param !== 0; return;
      case 'i': frame.chars.italic = param !== 0; return;
      case 'ul': case 'uld': case 'uldb': case 'ulw': case 'ulth': case 'ulwave': frame.chars.underline = param !== 0; return;
      case 'ulnone': frame.chars.underline = false; return;
      case 'strike': case 'striked': frame.chars.strike = param !== 0; return;
      case 'super': case 'sub': frame.chars.raised = true; return;
      case 'up': case 'dn': frame.chars.raised = (param ?? 0) !== 0; return;
      case 'nosupersub': frame.chars.raised = false; return;
      case 'cf': return;
      case 'cb': case 'highlight': case 'chcbpat': frame.chars.background = param ?? 0; return;
      case 'red': tableColour[0] = param ?? 0; return;
      case 'green': tableColour[1] = param ?? 0; return;
      case 'blue': tableColour[2] = param ?? 0; return;
      case 'fonttbl': frame.destination = 'fonttbl'; tableText = ''; return;
      case 'colortbl': frame.destination = 'colortbl'; tableText = ''; return;
      case 'expandedcolortbl': case 'stylesheet': case 'info': case 'nonshppict': case 'themedata': case 'colorschememapping': case 'latentstyles':
      case 'datastore': case 'xmlnstbl': case 'rsidtbl': case 'generatorx': case 'listpicture': case 'header': case 'footer': case 'footnote':
      case 'annotation': case 'object': case 'nextgraphic': case 'NeXTGraphic':
        if (word === 'NeXTGraphic') { images.push({ at: text.length, attachment: null }); dropAttachmentChar = true; }
        frame.destination = 'skip'; return;
      case 'shppict': return; // its \pict follows
      case 'pict': frame.destination = 'pict'; frame.pict = { hex: '', kind: '' }; return;
      case 'pngblip': case 'jpegblip': case 'emfblip': case 'wmetafile': case 'dibitmap': case 'wbitmap': if (frame.pict) frame.pict.kind = word; return;
      case 'field': frame.field = { url: null }; return;
      case 'fldinst': frame.destination = 'fldinst'; tableText = ''; return;
      case 'fldrslt': return;
      case 'listtable': frame.destination = 'listtable'; return;
      case 'list': if (frame.destination === 'listtable') frame.list = { id: null, levels: [] }; return;
      case 'listlevel': if (frame.list) frame.level = { format: '', nfc: 0, startAt: 1 }; return;
      case 'levelnfc': case 'levelnfcn': if (frame.level) frame.level.nfc = param ?? 0; return;
      case 'levelstartat': if (frame.level) frame.level.startAt = param ?? 1; return;
      case 'levelmarker': if (frame.level) { frame.destination = 'levelmarker'; tableText = ''; } return;
      case 'leveltext': case 'levelnumbers': case 'listname': frame.destination = 'skip'; return;
      case 'listid': if (frame.list) frame.list.id = param; else if (frame.override) frame.override.listId = param; return;
      case 'listoverridetable': frame.destination = 'listoverride'; return;
      case 'listoverride': frame.override = { listId: null, ls: null }; return;
      case 'listtext': return; // kept as text, as Cocoa's reader keeps it
      case 'pntext': return; // likewise, Windows' fallback marker text
      case 'pn': if (starred) { frame.destination = 'pn'; frame.pn = { style: 'bullet' }; } return;
      case 'pnlvlblt': if (frame.pn) frame.pn.style = 'bullet'; setParaPn(); return;
      case 'pndec': if (frame.pn) frame.pn.style = 'decimal'; setParaPn(); return;
      case 'pnlcltr': if (frame.pn) frame.pn.style = 'lowerAlpha'; setParaPn(); return;
      case 'pnucltr': if (frame.pn) frame.pn.style = 'upperAlpha'; setParaPn(); return;
      case 'pnlcrm': if (frame.pn) frame.pn.style = 'lowerRoman'; setParaPn(); return;
      case 'pnucrm': if (frame.pn) frame.pn.style = 'upperRoman'; setParaPn(); return;
      default:
        if (starred) frame.destination = 'skip';
        else if (CHAR_WORDS[word] !== undefined) { if (skipChars > 0) { skipChars--; return; } emit(CHAR_WORDS[word]); }
        return;
    }
  }

  /** A Windows `\pn` sets the enclosing paragraph's list style. */
  function setParaPn() {
    if (!frame.pn) return;
    const style = frame.pn.style;
    for (let k = stack.length - 1; k >= 0; k--) {
      if (stack[k].destination === 'pn') continue;
      stack[k].para.pn = style;
      break;
    }
  }

  function sameAttrs(a: CharAttrs, b: CharAttrs): boolean {
    return a.font === b.font && a.bold === b.bold && a.italic === b.italic && a.underline === b.underline && a.strike === b.strike
      && a.raised === b.raised && a.background === b.background && a.link === b.link;
  }
}

/** Whether the control word at `wordStart` (the position of its backslash) follows `\*` in its group. */
function isStarredGroup(bytes: Uint8Array, wordStart: number): boolean {
  return wordStart >= 2 && bytes[wordStart - 1] === 0x2a && bytes[wordStart - 2] === 0x5c;
}

function hexBytes(hex: string): Uint8Array {
  const clean = hex.replace(/[^0-9a-fA-F]/g, '');
  const out = new Uint8Array(clean.length >> 1);
  for (let k = 0; k < out.length; k++) out[k] = parseInt(clean.substr(k * 2, 2), 16);
  return out;
}

/** Removes Scrivener's formatting markers from the text, shortening the runs, paragraphs and image positions over them. */
function removeMarkers(text: string, runs: Run[], paragraphs: Paragraph[], images: ImageRef[]): string {
  const matches = [...text.matchAll(FORMATTING_MARKER)];
  if (!matches.length) return text;
  const cuts = matches.map((m) => [m.index!, m.index! + m[0].length] as [number, number]);
  const shift = (position: number) => {
    let removed = 0;
    for (const [from, to] of cuts) {
      if (position <= from) break;
      removed += Math.min(position, to) - from;
    }
    return position - removed;
  };
  for (const r of runs) { r.start = shift(r.start); r.end = shift(r.end); }
  for (const p of paragraphs) { p.start = shift(p.start); p.end = shift(p.end); }
  for (const im of images) im.at = shift(im.at);
  let out = '';
  let last = 0;
  for (const [from, to] of cuts) { out += text.slice(last, from); last = to; }
  out += text.slice(last);
  // Runs emptied by the cut go.
  for (let k = runs.length - 1; k >= 0; k--) if (runs[k].end <= runs[k].start) runs.splice(k, 1);
  return out;
}

// MARK: The colour table

function readColours(bytes: Uint8Array): ([number, number, number] | null)[] {
  // The colour table is simple enough to read on its own: `{\colortbl;\red255\green255\blue255;…}`.
  const ascii = latin1(bytes);
  const at = ascii.indexOf('{\\colortbl');
  if (at < 0) return [null];
  let depth = 0, end = at;
  for (; end < ascii.length; end++) {
    if (ascii[end] === '{') depth++;
    else if (ascii[end] === '}') { depth--; if (depth === 0) break; }
  }
  const body = ascii.slice(at + '{\\colortbl'.length, end);
  return body.split(';').slice(0, -1).map((entry) => {
    const r = /\\red(\d+)/.exec(entry), g = /\\green(\d+)/.exec(entry), b = /\\blue(\d+)/.exec(entry);
    return r && g && b ? [Number(r[1]), Number(g[1]), Number(b[1])] : null;
  });
}

const latin1 = (bytes: Uint8Array): string => {
  let s = '';
  for (let k = 0; k < bytes.length; k += 0x8000) s += String.fromCharCode(...bytes.subarray(k, k + 0x8000));
  return s;
};

/** A plain colour name for a highlight, from its hue (`RTFConverter.colourName`). */
export function colourName([r255, g255, b255]: [number, number, number]): string {
  const r = r255 / 255, g = g255 / 255, b = b255 / 255;
  const maxC = Math.max(r, g, b), minC = Math.min(r, g, b), delta = maxC - minC;
  if (delta < 0.08) return maxC > 0.85 ? 'white' : maxC < 0.2 ? 'black' : 'grey';
  let hue: number;
  if (maxC === r) hue = (g - b) / delta;
  else if (maxC === g) hue = 2 + (b - r) / delta;
  else hue = 4 + (r - g) / delta;
  hue = (hue * 60) % 360;
  if (hue < 0) hue += 360;
  if (hue < 15 || hue >= 345) return 'red';
  if (hue < 40) return 'orange';
  if (hue < 70) return 'yellow';
  if (hue < 170) return 'green';
  if (hue < 200) return 'cyan';
  if (hue < 260) return 'blue';
  if (hue < 300) return 'purple';
  return 'pink';
}

// MARK: The conversion (RTFConverter.convert)

/** `RTFConverter.listStyle`: the dialect's list style for a level's marker format ("{disc}", "{lower-roman}", "{decimal})"),
 *  else from `\levelnfc`. */
function listStyle(format: string, nfc: number): ListStyle {
  const name = format;
  if (name) {
    if (name.includes('circle')) return 'circle';
    if (name.includes('hyphen')) return 'dash';
    if (name.includes('lower-roman')) return 'lowerRoman';
    if (name.includes('upper-roman')) return 'upperRoman';
    if (name.includes('lower-alpha') || name.includes('lower-latin')) return 'lowerAlpha';
    if (name.includes('upper-alpha') || name.includes('upper-latin')) return 'upperAlpha';
    if (name.includes('decimal') || name.includes('octal') || name.includes('hex')) return name.endsWith(')') ? 'decimalParen' : 'decimal';
    return 'bullet';
  }
  switch (nfc) {
    case 0: return 'decimal';
    case 1: return 'upperRoman';
    case 2: return 'lowerRoman';
    case 3: return 'upperAlpha';
    case 4: return 'lowerAlpha';
    default: return 'bullet';
  }
}

/** `RTFConverter.listMarker`: optional leading tabs or spaces, a bullet or number, then a tab; the length in UTF-16. */
function typedListMarker(text: string): { style: ListStyle; number: number; length: number } | null {
  const indent = /^[\t ]*/.exec(text)![0];
  const rest = text.slice(indent.length);
  const tab = rest.slice(0, 12).indexOf('\t');
  if (tab < 0) return null;
  const marker = rest.slice(0, tab);
  const length = indent.length + marker.length + 1;
  if (['•', '▪', '■', '◆', '♦', '✓', '✔', '□', '⁌'].includes(marker)) return { style: 'bullet', number: 1, length };
  if (['◦', '○'].includes(marker)) return { style: 'circle', number: 1, length };
  if (['–', '⁃', '-', '—'].includes(marker)) return { style: 'dash', number: 1, length };
  const parsed = parseListMarker(marker + '\t');
  return parsed ? { style: parsed.style, number: parsed.number, length } : null;
}

/** `RTFConverter.verseParagraphs`: the paragraphs that are lines of verse, by where each starts, with their indent
 *  steps — runs of three or more paragraphs (not list items, not scene breaks, not right-aligned) that each hold a line
 *  separator, or whose head indents step (two or more distinct indents, each equal to its first-line indent, so no
 *  paragraph indent of its own, and every line at most 70 characters). A line's steps are its head indent over the run's
 *  smallest non-zero one, six at most. Never by shortness alone: short paragraphs are dialogue more often than verse. */
export function verseParagraphs(text: string, paragraphs: readonly Paragraph[]): Map<number, number> {
  interface Line { start: number; head: number; first: number; breaks: boolean; short: boolean }
  // Every paragraph with text (an empty one parts stanzas, never a run), or null where one can't be verse.
  const lines: (Line | null)[] = [];
  for (const paragraph of paragraphs) {
    let end = paragraph.end;
    while (end > paragraph.start && /[\n\r\u2029\u0085]/.test(text[end - 1])) end--;
    const paraText = text.slice(paragraph.start, end);
    const trimmed = trimCFWhitespaceAndNewlines(paraText);
    if (trimmed === '') continue;
    const style = paragraph.para;
    const candidate = !isSceneBreakMarker(trimmed) && style.ls === null && style.pn === null && typedListMarker(paraText) === null
      && style.align !== 'right' && !trimmed.startsWith('<$');
    // Points, as NSParagraphStyle gives them (twips ÷ 20).
    lines.push(candidate ? { start: paragraph.start, head: style.li / 20, first: (style.li + style.fi) / 20, breaks: paraText.includes('\u2028'),
      short: trimmed.split('\u2028').filter((piece) => piece !== '').every((piece) => graphemes(piece).length <= 70) } : null);
  }
  const result = new Map<number, number>();
  // Each rule's runs: consecutive paragraphs it applies to.
  const mark = (applies: (l: Line) => boolean, qualifies: (run: Line[]) => boolean) => {
    let i = 0;
    while (i < lines.length) {
      const first = lines[i];
      if (!first || !applies(first)) { i++; continue; }
      const run = [first];
      let j = i + 1;
      while (j < lines.length) {
        const line = lines[j];
        if (!line || !applies(line)) break;
        run.push(line);
        j++;
      }
      if (run.length >= 3 && qualifies(run)) {
        const positive = run.map((l) => l.head).filter((h) => h > 0.5);
        const step = positive.length ? Math.min(...positive) : 0;
        for (const line of run) if (!result.has(line.start)) result.set(line.start, step > 0 ? Math.min(MAX_VERSE_INDENT, Math.round(line.head / step)) : 0);
      }
      i = j;
    }
  };
  mark((l) => l.breaks, () => true);
  mark((l) => l.short && l.first === l.head && !result.has(l.start), (run) => new Set(run.map((l) => l.head)).size >= 2);
  return result;
}

export function isSceneBreakMarker(text: string): boolean {
  const compact = text.replace(/ /g, '');
  if (!compact.length || [...compact].length > 5) return false;
  return [...compact].every((c) => '#*~§•⁂'.includes(c));
}

function anchorKind(url: string): RTFAnchorKind | null {
  const m = /^([a-z][a-z0-9+.-]*):\/\/([^/?#]*)/i.exec(url);
  const scheme = (m?.[1] ?? url.split(':')[0]).toLowerCase();
  switch (scheme) {
    case 'scrivcmt': return m && m[2] ? { type: 'scrivenerComment', uuid: m[2].toUpperCase() } : null;
    case 'scrivlnk': return m && m[2] ? { type: 'documentLink', uuid: m[2].toUpperCase() } : null;
    case 'http': case 'https': case 'mailto': return { type: 'webLink', url };
    default: return null;
  }
}

const plainStyle = (): InlineStyle => ({ italic: false, bold: false, smallCaps: false, underline: false, highlight: null, color: null });

function lastStyle(inlines: Inline[]): InlineStyle {
  for (let k = inlines.length - 1; k >= 0; k--) {
    const inline = inlines[k];
    if (inline.type === 'text') return inline.style;
  }
  return plainStyle();
}

function trimTrailingSpaces(inlines: Inline[]) {
  while (inlines.length) {
    const last = inlines[inlines.length - 1];
    if (last.type !== 'text') return;
    const trimmed = last.text.replace(/ +$/, '');
    if (!trimmed) inlines.pop();
    else { inlines[inlines.length - 1] = { ...last, text: trimmed }; return; }
  }
}

/** A paragraph as the attributed string holds it: its text (terminator dropped, tabs as spaces), the indent from the
 *  text margin in inches (`max(headIndent, firstLineHeadIndent)`), its alignment, and its runs' emphasis — what a script
 *  classifier reads (`scrivenerScript.ts`). */
export interface RTFParagraph {
  /** The text with tabs as spaces (`rawText` keeps them; the two are the same length). */
  text: string;
  rawText: string;
  /** Inches from the text margin: the larger of the two indents, and the first line's own. */
  indent: number;
  firstIndent: number;
  align: 'left' | 'center' | 'right' | 'justify';
  runs: { text: string; bold: boolean; italic: boolean; underline: boolean }[];
}

export function rtfParagraphs(bytes: Uint8Array): RTFParagraph[] {
  const attributed = read(bytes);
  const text = attributed.text;
  const out: RTFParagraph[] = [];
  for (const paragraph of attributed.paragraphs) {
    let end = paragraph.end;
    while (end > paragraph.start && /[\n\r\u2029\u0085]/.test(text[end - 1])) end--;
    const runs: RTFParagraph['runs'] = [];
    let position = paragraph.start;
    while (position < end) {
      const run = attributed.runs.find((r) => position >= r.start && position < r.end);
      const stop = Math.min(run ? run.end : end, end);
      runs.push({ text: text.slice(position, stop).replace(/\t/g, ' ').replace(/￼/g, ''), bold: !!run?.attrs.bold, italic: !!run?.attrs.italic, underline: !!run?.attrs.underline });
      position = stop;
    }
    const style = paragraph.para;
    out.push({ text: runs.map((r) => r.text).join(''), rawText: text.slice(paragraph.start, end).replace(/￼/g, ''),
      indent: Math.max(style.li, style.li + style.fi) / 1440, firstIndent: (style.li + style.fi) / 1440, align: style.align, runs });
  }
  return out;
}

/** `RTFConverter.convert`. */
export function convertRTF(bytes: Uint8Array): RTFConversion {
  const attributed = read(bytes);
  const colours = readColours(bytes);
  const issues = attributed.issues;
  if (latin1(bytes).includes('Scrv_')) issues.add('Scrivener inline annotations or footnotes found; their markers were kept as text');
  const text = attributed.text;
  const blocks: Block[] = [];
  const anchorKinds: RTFAnchorKind[] = [];
  const anchorKeys: string[] = [];
  const attachments: RTFAttachment[] = [];
  // Anchors open in the current paragraph; links and highlights may overlap.
  let openAnchors = new Map<string, number>();
  // List markers become the dialect's own, so the source text is compared without them.
  const markers: [number, number][] = [];
  const listCounts = new Map<number, number>();
  const lists = listTableOf(bytes);

  const runAt = (position: number): Run | null => {
    for (const r of attributed.runs) if (position >= r.start && position < r.end) return r;
    return null;
  };
  const verse = verseParagraphs(attributed.text, attributed.paragraphs);
  if (verse.size > 0) issues.add('Poems set as verse, their lines stepped by their indents');
  // Whether the last block appended is a stanza still open (no empty paragraph since its last line).
  let stanzaOpen = false;

  for (const paragraph of attributed.paragraphs) {
    let contentEnd = paragraph.end;
    // Drop the paragraph terminator.
    while (contentEnd > paragraph.start && /[\n\r\u2029\u0085]/.test(text[contentEnd - 1])) contentEnd--;
    const contentStart0 = paragraph.start;
    const paraText = text.slice(contentStart0, contentEnd);
    const trimmed = paraText.trim();
    if (!trimmed) { stanzaOpen = false; continue; }
    const style = paragraph.para;
    const verseSteps = verse.get(paragraph.start);
    if (isSceneBreakMarker(trimmed)) { blocks.push({ kind: { type: 'sceneBreak' }, inlines: [], alignment: null }); continue; }

    // A list item: a paragraph in a text list (the RTF's own list table), or one typed with a marker and a tab.
    let listItem: { style: ListStyle; number: number } | null = null;
    const typed = typedListMarker(paraText);
    const listLevels = style.ls !== null ? lists.get(style.ls) : undefined;
    if (listLevels && listLevels.length) {
      const level = listLevels[0];
      const number = (listCounts.get(style.ls!) ?? level.startAt - 1) + 1;
      listCounts.set(style.ls!, number);
      listItem = { style: listStyle(level.format, level.nfc), number };
    } else if (style.pn) {
      const number = (listCounts.get(-1) ?? 0) + 1;
      listCounts.set(-1, number);
      listItem = { style: style.pn, number: typed && typed.number > 0 && typed.style === style.pn ? typed.number : number };
    } else if (typed) {
      listItem = { style: typed.style, number: typed.number };
    }
    let contentStart = contentStart0;
    if (listItem && typed) {
      markers.push([contentStart, contentStart + typed.length]);
      contentStart += typed.length;
    }
    const headIndent = style.li / 20, firstLine = (style.li + style.fi) / 20;
    const isQuote = listItem === null && headIndent >= 30 && firstLine >= 30 && style.align !== 'center' && style.align !== 'right';
    const alignment: Alignment | null = style.align === 'center' ? 'center' : style.align === 'right' ? 'right' : null;

    const inlines: Inline[] = [];
    let leadingTrimmed = false;
    // Walk the runs over the content, as enumerateAttributes does.
    let position = contentStart;
    while (position < contentEnd) {
      const run = runAt(position);
      const end = Math.min(run ? run.end : contentEnd, contentEnd);
      const attrs = run?.attrs;
      let runText = text.slice(position, end);
      const runStart = position;
      position = end;
      if (!leadingTrimmed) {
        const stripped = runText.replace(/^[ \t]+/, '');
        if (!stripped) continue;
        runText = stripped;
        leadingTrimmed = true;
      }
      runText = AnchorSentinels.neutralise(runText.replace(/\t/g, ' '));

      const inlineStyle = plainStyle();
      if (attrs?.italic) inlineStyle.italic = true;
      if (attrs?.bold) inlineStyle.bold = true;
      if (attrs?.underline) inlineStyle.underline = true;
      if (attrs?.strike) issues.add('Strikethrough removed (text kept)');
      if (attrs?.raised) issues.add('Superscript or subscript removed (text kept)');
      for (const image of attributed.images) {
        if (image.at >= runStart && image.at < end) {
          if (image.attachment) attachments.push(image.attachment);
          issues.add('Inline image moved to the fragment\'s attachments');
        }
      }
      runText = runText.replace(/￼/g, '');

      // Links, linked comments and highlights: wrap runs in sentinels, closed when they end.
      const kinds = new Map<string, RTFAnchorKind>();
      if (attrs?.link) { const k = anchorKind(attrs.link); if (k) kinds.set(anchorKindKey(k), k); }
      // Scrivener ends a highlight with a white background (\cb1), which is not a highlight.
      if (attrs && attrs.background > 0) {
        const colour = colours[attrs.background];
        if (colour) { const name = colourName(colour); if (name !== 'white') { const k: RTFAnchorKind = { type: 'highlight', color: name }; kinds.set(anchorKindKey(k), k); } }
      }
      for (const [key, index] of [...openAnchors]) {
        if (!kinds.has(key)) {
          inlines.push({ type: 'text', text: AnchorSentinels.marker('end', index), style: lastStyle(inlines) });
          openAnchors.delete(key);
        }
      }
      for (const [key, kind] of kinds) {
        if (!openAnchors.has(key)) {
          anchorKinds.push(kind);
          anchorKeys.push(key);
          openAnchors.set(key, anchorKinds.length - 1);
          inlines.push({ type: 'text', text: AnchorSentinels.marker('start', anchorKinds.length - 1), style: inlineStyle });
        }
      }
      // Line separators inside a paragraph become hard breaks.
      runText.split('\u2028').forEach((piece, n) => {
        if (n > 0) inlines.push({ type: 'lineBreak' });
        if (piece) inlines.push({ type: 'text', text: piece, style: inlineStyle });
      });
    }
    for (const [, index] of openAnchors) inlines.push({ type: 'text', text: AnchorSentinels.marker('end', index), style: lastStyle(inlines) });
    openAnchors = new Map();
    trimTrailingSpaces(inlines);
    if (!inlines.length) continue;
    if (verseSteps !== undefined) {
      // A line of verse: stepped by its indent, joined to the open stanza (an empty paragraph ended the last).
      const line: Inline[] = (verseSteps > 0 && alignment === null ? [{ type: 'text', text: '\t'.repeat(verseSteps), style: plainInlineStyle() } as Inline] : []).concat(inlines);
      const last = blocks[blocks.length - 1];
      if (stanzaOpen && last && last.kind.type === 'verse' && last.alignment === alignment) last.inlines.push({ type: 'lineBreak' }, ...line);
      else blocks.push({ kind: { type: 'verse' }, inlines: line, alignment });
      stanzaOpen = true;
      continue;
    }
    stanzaOpen = false;
    if (listItem) blocks.push({ kind: { type: 'listItem', style: listItem.style, number: listItem.number }, inlines, alignment: null });
    else blocks.push({ kind: { type: isQuote ? 'blockQuote' : 'paragraph' }, inlines, alignment: isQuote ? null : alignment });
  }

  const { text: markdown, anchors: found } = AnchorSentinels.extract(serialize(blocks));
  const anchors = mergeAdjacent(found.filter((a) => a.index < anchorKinds.length).map((a) => ({ kind: anchorKinds[a.index], range: a.range })));
  let source = text;
  for (const [from, to] of markers.slice().reverse()) source = source.slice(0, from) + ' ' + source.slice(to);
  return { markdown, sourcePlainText: source, anchors, attachments, issues: issues.messages };
}

/** A link split across paragraphs or styles yields several ranges; join those of the same target. */
function mergeAdjacent(anchors: RTFAnchor[]): RTFAnchor[] {
  const result: RTFAnchor[] = [];
  const lastIndex = new Map<string, number>();
  for (const anchor of [...anchors].sort((a, b) => a.range[0] - b.range[0])) {
    const key = anchorKindKey(anchor.kind);
    const i = lastIndex.get(key);
    if (i !== undefined && anchor.range[0] <= result[i].range[1] + 4) {
      result[i].range = [result[i].range[0], Math.max(result[i].range[1], anchor.range[1])];
    } else {
      result.push({ kind: anchor.kind, range: [anchor.range[0], anchor.range[1]] });
      lastIndex.set(key, result.length - 1);
    }
  }
  return result;
}

/** The list table by `\ls` number: each list's levels (marker format, `\levelnfc`, start). */
function listTableOf(bytes: Uint8Array): Map<number, { format: string; nfc: number; startAt: number }[]> {
  const ascii = latin1(bytes);
  const out = new Map<number, { format: string; nfc: number; startAt: number }[]>();
  const tableAt = ascii.indexOf('{\\*\\listtable');
  if (tableAt < 0) return out;
  const table = groupAt(ascii, tableAt);
  const lists = new Map<number, { format: string; nfc: number; startAt: number }[]>();
  for (const listGroup of groups(table, '{\\list')) {
    if (listGroup.startsWith('{\\listlevel') || listGroup.startsWith('{\\listname') || listGroup.startsWith('{\\listoverride')) continue;
    const id = /\\listid(-?\d+)/.exec(listGroup);
    const levels = groups(listGroup, '{\\listlevel').map((level) => {
      const marker = /\{\\\*\\levelmarker ([^}]*)\}/.exec(level);
      const nfc = /\\levelnfc(\d+)/.exec(level);
      const start = /\\levelstartat(\d+)/.exec(level);
      return { format: marker ? marker[1].replace(/\\([{}])/g, '$1').trim() : '', nfc: nfc ? Number(nfc[1]) : 0, startAt: start ? Number(start[1]) : 1 };
    });
    if (id) lists.set(Number(id[1]), levels);
  }
  const overridesAt = ascii.indexOf('{\\*\\listoverridetable');
  if (overridesAt >= 0) {
    for (const o of groups(groupAt(ascii, overridesAt), '{\\listoverride')) {
      const id = /\\listid(-?\d+)/.exec(o), ls = /\\ls(\d+)/.exec(o);
      if (id && ls && lists.has(Number(id[1]))) out.set(Number(ls[1]), lists.get(Number(id[1]))!);
    }
  }
  return out;
}

/** The balanced group starting at `at` (its braces included). */
function groupAt(s: string, at: number): string {
  let depth = 0;
  for (let k = at; k < s.length; k++) {
    if (s[k] === '{') depth++;
    else if (s[k] === '}') { depth--; if (depth === 0) return s.slice(at, k + 1); }
  }
  return s.slice(at);
}

/** The top-level groups inside `s` (its own braces removed) starting with `prefix`. */
function groups(s: string, prefix: string): string[] {
  const inner = s.slice(1, -1);
  const out: string[] = [];
  let depth = 0, start = -1;
  for (let k = 0; k < inner.length; k++) {
    if (inner[k] === '{') { if (depth === 0) start = k; depth++; }
    else if (inner[k] === '}') { depth--; if (depth === 0 && start >= 0) { const g = inner.slice(start, k + 1); if (g.startsWith(prefix)) out.push(g); start = -1; } }
  }
  return out;
}

/** Plain text of RTF, as used for comment bodies and notes (`RTFConverter.plainText`), trimmed. */
export function rtfPlainText(bytes: Uint8Array): string {
  return read(bytes).text.trim();
}
